import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { ArrowRight, Mail } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);
  const [resendDone, setResendDone] = useState(false);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(""); setResendDone(false); setBusy(true);
    try {
      const user = await login(email, password);
      const dest = user.role === "admin" ? "/dashboard/admin"
                : user.role === "vendor" ? "/dashboard/vendor"
                : "/dashboard/lovers";
      navigate(loc.state?.from || dest, { replace: true });
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const emailMeMyLogin = async () => {
    if (!email || !email.includes("@")) {
      setErr("Type your email above first, then we'll send you a reset link.");
      return;
    }
    setResendBusy(true);
    setErr("");
    try {
      await api.post("/auth/forgot", { email });
      setResendDone(true);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Couldn't send the email right now.");
    } finally {
      setResendBusy(false);
    }
  };

  const showEmailMeCTA = !!err && !resendDone;

  return (
    <Layout noFooter>
      <div className="min-h-[calc(100vh-5rem)] grid lg:grid-cols-2">
        <div className="hidden lg:block relative bg-coral/15 qld-grain">
          <img
            src="https://customer-assets.emergentagent.com/job_lovers-collective/artifacts/1j68g7vk_love%2C%20in%20full%20.jpg"
            alt="" className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/40 to-transparent"/>
          <div className="absolute bottom-12 left-12 right-12 text-cream">
            <p className="qld-overline !text-butter mb-3">welcome back</p>
            <p className="font-serif text-4xl xl:text-5xl leading-tight max-w-md text-cream"
               style={{ fontVariationSettings: '"opsz" 144, "wght" 700, "SOFT" 30, "WONK" 1' }}>
              Because the wedding space should feel welcoming for every love story.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-center p-8 md:p-16">
          <div className="w-full max-w-md">
            <h1 className="font-serif text-4xl md:text-5xl text-wine mb-2">Log in</h1>
            <p className="text-wine/70 mb-10">Lovers and wedding pros, you're welcome here.</p>

            <button data-testid="login-google-btn" onClick={handleGoogle}
              className="hidden">hidden</button>
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-ink mb-1.5">Email</label>
                <input data-testid="login-email-input" type="email" required value={email}
                  onChange={(e)=>setEmail(e.target.value)} className="qld-input" placeholder="you@example.com"/>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium text-ink">Password</label>
                  <Link to="/forgot-password" className="text-xs text-wine underline decoration-2 underline-offset-4 hover:no-underline" data-testid="login-forgot-link">
                    Forgot?
                  </Link>
                </div>
                <input data-testid="login-password-input" type="password" required value={password}
                  onChange={(e)=>setPassword(e.target.value)} className="qld-input" placeholder="••••••••"/>
              </div>
              {err && <p data-testid="login-error" className="text-destructive text-sm">{err}</p>}

              {resendDone && (
                <div
                  className="bg-mustard border-2 border-black rounded-2xl p-4 flex items-start gap-3"
                  data-testid="login-resend-done"
                >
                  <Mail className="w-5 h-5 mt-0.5 shrink-0" strokeWidth={1.5}/>
                  <div>
                    <p className="font-medium text-ink text-sm">Login link on the way.</p>
                    <p className="text-xs text-black/70 leading-snug mt-0.5">
                      If <strong>{email}</strong> is on file, you'll receive an email shortly with a reset link.
                      Check spam if you don't see it in 5 minutes.
                    </p>
                  </div>
                </div>
              )}

              <button data-testid="login-submit-btn" type="submit" disabled={busy} className="qld-btn-primary w-full">
                {busy ? "Signing in…" : <>Log in <ArrowRight className="w-4 h-4" strokeWidth={1.5}/></>}
              </button>

              {showEmailMeCTA && (
                <button
                  type="button"
                  onClick={emailMeMyLogin}
                  disabled={resendBusy}
                  data-testid="login-email-me-btn"
                  className="lif-btn lif-btn-mustard w-full text-sm"
                >
                  <Mail className="w-4 h-4" strokeWidth={1.5}/>
                  {resendBusy ? "Sending…" : "Email me my login"}
                </button>
              )}
            </form>

            <p className="text-sm text-muted text-center mt-8">
              New here? <Link to="/join" className="qld-link" data-testid="login-to-join">Create an account</Link>
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
